import { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, MessageSquare, Users, UserCircle, Bell, Settings, Shield, BookOpen } from 'lucide-react';

const FAQ_DATA = [
  {
    category: "Getting Started",
    icon: BookOpen,
    questions: [
      {
        q: "How do I join a community?",
        a: "Click on 'Communities' in the sidebar, then click 'Open Chat' on any community you want to join. You'll automatically become a member when you view or post in a community."
      },
      {
        q: "How do I post a message?",
        a: "Navigate to any community, type your message in the text box at the top, and click 'Post'. Your message will appear instantly in the chat."
      },
      {
        q: "How do I reply to someone's message?",
        a: "Click the 'Reply' button below any message. A text input will appear where you can type your reply. Press Enter or click the send button to post your reply."
      }
    ]
  },
  {
    category: "Anonymous Posting",
    icon: UserCircle,
    questions: [
      {
        q: "How do I post anonymously?",
        a: "When composing a message, toggle the switch next to 'Post as [Your Name]' to switch to anonymous mode. Your anonymous username will be shown instead of your real name."
      },
      {
        q: "What is an anonymous username?",
        a: "Your anonymous username is a custom name that's shown when you post anonymously. You can set it in your Profile settings. Others won't know who you really are!"
      },
      {
        q: "How often can I change my anonymous username?",
        a: "You can change your anonymous username once every 15 days. This prevents misuse while still giving you flexibility to update it occasionally."
      },
      {
        q: "Can people find out who I am when I post anonymously?",
        a: "No! Your real identity is completely hidden when posting anonymously. Only your anonymous username and chosen avatar are visible to others."
      }
    ]
  },
  {
    category: "Profile & Avatar",
    icon: Users,
    questions: [
      {
        q: "How do I change my profile picture?",
        a: "Go to Profile, click 'Edit Profile', then click the camera icon to upload a photo, or click the smile icon to choose an avatar."
      },
      {
        q: "What is the avatar for?",
        a: "Your chosen avatar is displayed when you post anonymously. It helps give your anonymous identity a visual representation without revealing who you are."
      },
      {
        q: "How do I update my anonymous username?",
        a: "Go to Profile > Edit Profile. You'll see the 'Anonymous Username' field where you can enter your preferred name. Remember, you can only change it once every 15 days!"
      }
    ]
  },
  {
    category: "Notifications",
    icon: Bell,
    questions: [
      {
        q: "How do notifications work?",
        a: "Click the bell icon in the top navigation bar to view your notifications. You'll be notified when someone replies to your messages."
      },
      {
        q: "Can I turn off notifications?",
        a: "Currently, notifications are enabled by default to keep you updated on replies. More notification settings will be available soon."
      }
    ]
  },
  {
    category: "Privacy & Safety",
    icon: Shield,
    questions: [
      {
        q: "Is my data safe?",
        a: "Yes! All your data is securely stored and encrypted. We never share your personal information with third parties."
      },
      {
        q: "Can I delete my messages?",
        a: "Message deletion is coming soon. For now, think carefully before posting!"
      },
      {
        q: "What if someone is being inappropriate?",
        a: "We encourage respectful discussion. Reporting features will be available soon. In the meantime, please be kind to your fellow students."
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 px-4 -mx-4 transition-colors"
      >
        <span className="text-sm font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-sm text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

function FAQSection({ category, icon: Icon, questions }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-indigo-600" />
        </div>
        <h3 className="font-semibold text-gray-900">{category}</h3>
      </div>
      <div className="p-4">
        {questions.map((item) => (
          <FAQItem key={`${category}-${item.q.substring(0, 20)}`} question={item.q} answer={item.a} />
        ))}
      </div>
    </div>
  );
}

export default function HelpPage() {
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'DM Sans' }}>
          Help & Guide
        </h1>
        <p className="text-gray-600">Everything you need to know about using UniConnect</p>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-2xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <HelpCircle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold mb-2">Quick Start Guide</h2>
            <ol className="space-y-2 text-white/90 text-sm">
              <li>1. <strong>Join communities</strong> - Browse and click on any course community to join</li>
              <li>2. <strong>Post messages</strong> - Share questions, notes, or start discussions</li>
              <li>3. <strong>Go anonymous</strong> - Toggle anonymous mode to post without revealing your identity</li>
              <li>4. <strong>Customize your profile</strong> - Set your anonymous username and choose an avatar</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
          <UserCircle className="w-8 h-8 text-purple-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Anonymous Identity</h3>
          <p className="text-sm text-gray-600">Post freely without revealing who you are. Your chosen username and avatar protect your privacy.</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
          <MessageSquare className="w-8 h-8 text-blue-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Real-time Chat</h3>
          <p className="text-sm text-gray-600">Messages appear instantly. Reply to others and have meaningful discussions with classmates.</p>
        </div>
        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
          <Users className="w-8 h-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Course Communities</h3>
          <p className="text-sm text-gray-600">Each course has its own community. Share resources, ask questions, and collaborate.</p>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
          Frequently Asked Questions
        </h2>
        {FAQ_DATA.map((section) => (
          <FAQSection
            key={section.category}
            category={section.category}
            icon={section.icon}
            questions={section.questions}
          />
        ))}
      </div>

      {/* Contact Support */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center">
        <Settings className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900 mb-2">Still need help?</h3>
        <p className="text-sm text-gray-600 mb-4">
          Can't find what you're looking for? We're here to help!
        </p>
        <p className="text-sm text-indigo-600 font-medium">
          Contact support: uniconnecthelp@gmail.com
        </p>
      </div>
    </div>
  );
}
